import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Users, Trophy, Heart, Lightbulb, Zap } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const PickleballParadox = () => {
  const pickleballJsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "The Paradox of Pickleball - Leadership Program",
    "description": "An innovative leadership development program that draws powerful parallels between mastering pickleball and mastering leadership.",
    "url": "https://www.paradoxedge.com/pickleballparadox",
    "organizer": {
      "@type": "Organization",
      "name": "ParadoxEdge"
    },
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    }
  };

  const handleScheduleCall = () => {
    window.open("https://calendly.com/kentfrazier/30-minutes", "_blank");
  };


  const parallels = [
    { title: "Court Awareness", description: "Reading the game requires seeing the whole court - just like leaders need to see the whole system", icon: Target, color: "from-amber-500 to-orange-600" },
    { title: "Patience & Timing", description: "The best shots come from waiting for the right moment - great leaders know when to act", icon: Lightbulb, color: "from-orange-500 to-red-600" },
    { title: "Partnership", description: "Doubles requires seamless collaboration - exactly what high-performing teams need", icon: Users, color: "from-rose-500 to-pink-600" },
    { title: "Adaptability", description: "Every point is different - successful leaders adapt to each unique situation", icon: Zap, color: "from-purple-500 to-indigo-600" }
  ];

  return (
    <>
      <SEOHead
        noindex
        title="The Paradox of Pickleball - Leadership Development Program"
        description="An innovative leadership program using pickleball to teach strategic thinking, team dynamics, and resilience. Perfect for executive teams and leadership retreats."
        canonicalUrl="/pickleballparadox"
        keywords="pickleball leadership, executive team building, leadership retreat, innovative leadership training, team development program"
        jsonLd={pickleballJsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container-mobile pt-8 sm:pt-12 pb-24">
          {/* Hero Section */}
          <div className="text-center mb-16 sm:mb-20 max-w-3xl mx-auto">
            <p className="text-primary font-semibold tracking-[0.15em] uppercase text-sm mb-4">
              Featured Program
            </p>
            
            <h1 className="text-foreground text-4xl sm:text-5xl lg:text-6xl mb-6">
              The Paradox of <span className="text-primary">Pickleball</span>
            </h1>
            
            <p className="text-foreground/80 text-xl font-display italic mb-6">
              Leadership Lessons from America's Fastest Growing Sport
            </p>
            
            <p className="text-foreground/60 leading-relaxed mb-8 text-lg">
              What if the secret to exceptional leadership could be found on a pickleball court? 
              This innovative program draws powerful parallels between mastering the game and mastering leadership.
            </p>
            
            <Button 
              onClick={handleScheduleCall}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105"
            >
              Begin a Conversation <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* The Paradox */}
          <div className="mb-20 max-w-4xl mx-auto">
            <Card className="bg-foreground text-background overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />
              <CardContent className="p-8 sm:p-12">
                <h2 className="text-background text-2xl sm:text-3xl font-display text-center mb-8">
                  The Paradox Revealed
                </h2>
                <div className="space-y-6 text-background/80 leading-relaxed text-lg">
                  <p>
                    Pickleball appears simple: just hit the ball over the net. Yet beneath this simplicity lies 
                    profound complexity requiring strategy, patience, positioning, and partnership.
                  </p>
                  <p>
                    Leadership presents the same paradox. The fundamentals seem straightforward, but mastery requires 
                    understanding the intricate dynamics of human behavior, systems, and strategic thinking.
                  </p>
                  <p className="text-xl font-display text-primary text-center pt-4 italic">
                    Both demand that you master the fundamentals while navigating infinite complexity.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Core Parallels */}
          <div className="mb-20 max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-primary font-semibold tracking-[0.15em] uppercase text-sm mb-2">
                On the Court
              </p>
              <h2 className="text-foreground text-2xl sm:text-3xl font-display">
                Leadership Parallels
              </h2>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2">
              {parallels.map((parallel, index) => (
                <Card key={index} className="bg-card border border-border hover:border-primary/40 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 group overflow-hidden">
                  <div className={`h-1 bg-gradient-to-r ${parallel.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <CardContent className="p-6">
                    <div className="flex items-start gap-5">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${parallel.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <parallel.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-foreground font-medium text-lg mb-2">{parallel.title}</h3>
                        <p className="text-foreground/60 leading-relaxed">{parallel.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* What You'll Learn */}
          <div className="mb-20 max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-foreground text-2xl sm:text-3xl font-display">
                What You'll Discover
              </h2>
            </div>
            
            <div className="grid gap-8 md:grid-cols-3">
              {[
                { icon: Trophy, title: "Strategic Thinking", desc: "Think three shots ahead in business decisions" },
                { icon: Users, title: "Team Dynamics", desc: "Build partnerships that create competitive advantage" },
                { icon: Heart, title: "Resilience", desc: "Bounce back from setbacks stronger than before" }
              ].map((item, i) => (
                <Card key={i} className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
                  <CardContent className="p-8 text-center">
                    <item.icon className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="text-foreground font-display text-xl mb-3">{item.title}</h3>
                    <p className="text-foreground/60">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* The Experience */}
          <div className="mb-20 max-w-4xl mx-auto">
            <Card className="bg-card border border-border overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-primary via-orange-400 to-amber-400" />
              <CardContent className="p-8 sm:p-12">
                <h2 className="text-foreground text-2xl sm:text-3xl font-display text-center mb-10">
                  The Experience
                </h2>
                
                <div className="grid md:grid-cols-3 gap-8 mb-10">
                  {[
                    { step: "1", title: "On-Court Learning", desc: "Experience principles through guided play" },
                    { step: "2", title: "Leadership Debrief", desc: "Connect court to real business challenges" },
                    { step: "3", title: "Action Planning", desc: "Develop strategies to implement insights" }
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 font-display text-xl">
                        {item.step}
                      </div>
                      <h4 className="text-foreground font-medium mb-2">{item.title}</h4>
                      <p className="text-foreground/60 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
                
                <p className="text-center text-foreground/70 text-lg">
                  Perfect for executive teams, leadership retreats, and anyone ready to discover their edge.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary font-semibold tracking-[0.15em] uppercase text-sm mb-4">
              The Court is Calling
            </p>
            <h2 className="text-foreground text-3xl sm:text-4xl mb-4">Ready to Discover Your Edge?</h2>
            <p className="text-foreground/60 text-lg mb-8">
              Experience leadership development like never before.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleScheduleCall}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-full font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105"
              >
                Begin a Conversation <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default PickleballParadox;
